import AppLayout from "layouts/appLayout/AppLayout";
import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { OpportunityController } from "services/opportunity/controller";
import { Button, Card } from "stories";
import { Dialog } from "stories/components/dialog";
import { useFetchState } from "utils/customHooks/useFetchState";
import { useFileUpload } from "utils/customHooks/useFileUpload";
import { useToast } from "utils/customHooks/useToast";
import { getSubAssetModel } from "../edit/loanDetails/elements/overview/subAssetType/SubAssetType";
import classes from "./CreateOpportunity.module.scss";
import CreateOpportunityDetails from "./elements/createOpportunityDetails/CreateOpportunityDetails";
import LoanDetailsSection from "./elements/loanDetailsSection/LoanDetailsSection";

const opportunityController = OpportunityController();
const getOpportunityModel = () => ({
  images: [],
  loan_details: {
    amount_requested: "",
    mortgage_position: "",
    loan_type: "",
    asset_classes: getSubAssetModel(),
    refinance_loan: false,
  },
  opportunity_details: {
    project_purpose: "",
    address: "",
    city: "",
    country: "",
    postal_code: "",
    google_map_url: "",
    project_highlights: "",
    project_name: "",
    state_or_province: "",
    visibility: "",
  },
});

export const serializeSubAssets = (assetClasses) => {
  for (const key in assetClasses) {
    for (const subKey in assetClasses[key]) {
      if (assetClasses[key][subKey].length === 0) {
        delete assetClasses[key][subKey];
      }
    }
  }
  return assetClasses;
};

const CreateOpportunity = () => {
  const form = useForm({
    defaultValues: getOpportunityModel(),
  });
  const navigate = useNavigate();
  const [createdOpp, setCreatedOpp] = useState(null);
  const [fetchState, sendRequest, setLoading] = useFetchState();
  const { showToast } = useToast();
  const { handleFileFields } = useFileUpload();

  const handleCreateOpportunity = async (values) => {
    setLoading(true);
    return await handleFileFields(values).then((values) => {
      values.loan_details.asset_classes = serializeSubAssets(
        values.loan_details.asset_classes
      );
      sendRequest(opportunityController.create, values);
    });
  };

  const continueToOpProfile = () => {
    navigate(`/opportunities/${createdOpp}`);
  };
  const continueToOpList = () => {
    navigate(`/opportunities/`);
  };

  useEffect(() => {
    if (!fetchState.isLoading && !fetchState.isError && fetchState.data) {
      showToast("Opportunity created!", "success");
      form.reset();
      setCreatedOpp(fetchState.data.opportunity_id);
    }
  }, [fetchState, navigate]); // eslint-disable-line
  return (
    <AppLayout title={"Opportunities"} hideTopNav hideSidebar>
      <Helmet>
        <title>Create Opportunity | Fundever</title>
      </Helmet>
      <FormProvider {...form}>
        <Container className={classes["container"]}>
          <form onSubmit={form.handleSubmit(handleCreateOpportunity)}>
            <Row className={"mt-4"}>
              <Col>
                <div className={"page-title"}>Create Opportunity</div>
              </Col>
            </Row>
            <Row className={"mt-3"}>
              <Col>
                <Card>
                  <div className={classes["card-header"]}>
                    Create Opportunity Profile
                  </div>
                  <div className={classes["card-body"]}>
                    <section>
                      <div className={classes["section-title"]}>
                        Loan Details
                      </div>
                      <LoanDetailsSection />
                    </section>
                    <section>
                      <div className={classes["section-title"]}>
                        Opportunity Details
                      </div>
                      <CreateOpportunityDetails />
                    </section>
                  </div>
                </Card>
              </Col>
            </Row>
            <Row className={"mt-4"}>
              <Col xs={8} />
              <Col>
                <Button
                  outline
                  label={"Cancel"}
                  onClick={() => navigate("/opportunities")}
                />
              </Col>
              <Col>
                <Button
                  label={"Create Opportunity"}
                  loading={fetchState.isLoading}
                />
              </Col>
            </Row>
          </form>
        </Container>
      </FormProvider>

      <Dialog
        title="Opportunity Profile Created"
        description="Do you want to continue and provide the required details for this opportunity?"
        okText="Yes, Continue"
        cancelText="Save as draft for now"
        isOpen={createdOpp}
        onConfirm={continueToOpProfile}
        onCancel={continueToOpList}
        onClose={setCreatedOpp.bind(null, null)}
      />
    </AppLayout>
  );
};

export default CreateOpportunity;
